const ENCOUNTER_SCOPE = ['opd', 'ipd', 'er', 'ot', 'maternity', 'dialysis', 'daycare', 'dental'];
const STOCK_SCOPE = ['inventory', 'pharmacy'];
export const PERMISSION_MODULES = {
    'rx.read': [...ENCOUNTER_SCOPE, 'pharmacy'],
    'rx.write': ENCOUNTER_SCOPE,
    'order.read': ENCOUNTER_SCOPE,
    'order.write': ENCOUNTER_SCOPE,
    'encounter.read': ENCOUNTER_SCOPE,
    'encounter.read.own': ENCOUNTER_SCOPE,
    'encounter.write': ENCOUNTER_SCOPE,
    'encounter.close': ENCOUNTER_SCOPE,
    'encounter.cover': ENCOUNTER_SCOPE,
    'vitals.write': ENCOUNTER_SCOPE,
    'diagnosis.write': ENCOUNTER_SCOPE,
    'certificate.issue': ENCOUNTER_SCOPE,
    'emr.config.manage': ENCOUNTER_SCOPE,
    'therapy.manage': ['dialysis', 'daycare'],
    'order.late.post': ['ipd', 'lab', 'radiology'],
    'appointment.read': ['opd', 'lab'],
    'appointment.manage': ['opd', 'lab'],
    'product.read': STOCK_SCOPE,
    'product.manage': STOCK_SCOPE,
    'stock.read': STOCK_SCOPE,
    'stock.adjust': STOCK_SCOPE,
    'stock.adjust.approve': STOCK_SCOPE,
    'stock.take.manage': STOCK_SCOPE,
    'stock.batch.manage': STOCK_SCOPE,
    'stock.reprice': STOCK_SCOPE,
    'purchase.manage': STOCK_SCOPE,
    'purchase.grn': STOCK_SCOPE,
    'purchase.return': STOCK_SCOPE,
    'store.manage': STOCK_SCOPE,
    'supplier.payment.create': STOCK_SCOPE,
    'supplier.payment.approve': STOCK_SCOPE,
    'indent.request': ['pharmacy', 'inventory'],
    'indent.issue': ['pharmacy', 'inventory'],
    'mortality.read': ['ipd', 'er'],
    'report.read': ['core'],
    'dashboard.read': ['core'],
    'hr.self.read': ['core'],
    'report.read.all': ['reports'],
    'report.financial': ['reports'],
};
const LOCK_SAFE_CODES = new Set(['clinical.break_glass', 'radiology.worklist']);
const CLINICAL_CODES = new Set([
    'encounter.read', 'encounter.read.own', 'encounter.write', 'encounter.close', 'encounter.cover',
    'vitals.write', 'diagnosis.write', 'rx.write', 'rx.read', 'order.write', 'order.read',
    'clinical.sensitive.read', 'clinical.break_glass', 'certificate.issue',
    'encounter.cosign', 'encounter.backdate',
    'admission.read', 'admission.manage', 'cpoe.write', 'emar.administer', 'emar.read',
    'discharge.summary.write', 'nursing.note.write', 'mortality.read',
    'er.read', 'er.manage', 'ot.read', 'ot.manage',
    'lab.order.read', 'lab.result.enter', 'lab.result.verify', 'lab.result.amend',
    'radiology.worklist', 'radiology.report.write', 'radiology.report.verify',
    'mrd.read', 'mrd.code', 'mrd.release', 'patient.document.manage',
    'blood_bank.manage', 'maternity.manage', 'therapy.manage', 'dental.manage',
]);
export function segmentReadAccess(code) {
    if (LOCK_SAFE_CODES.has(code))
        return true;
    const segments = code.split('.');
    return segments.includes('read') || segments.includes('view');
}
const p = (code, module, group, description, sensitive = false) => {
    const modules = PERMISSION_MODULES[code] ?? [module];
    return {
        code,
        module: modules[0] ?? module,
        modules,
        group,
        description,
        sensitive,
        access: segmentReadAccess(code) ? 'read' : 'write',
        clinical: CLINICAL_CODES.has(code),
    };
};
export const PERMISSIONS = [
    // Tenant administration
    p('tenant.settings.read', 'core', 'Administration', 'View the facility settings'),
    p('tenant.settings.manage', 'core', 'Administration', 'Change the facility settings', true),
    p('branch.read', 'core', 'Administration', 'View branches'),
    p('branch.manage', 'core', 'Administration', 'Create and update branches'),
    p('department.read', 'core', 'Administration', 'View departments and units'),
    p('department.manage', 'core', 'Administration', 'Create and update departments and units'),
    p('user.read', 'core', 'Administration', 'View staff accounts'),
    p('user.manage', 'core', 'Administration', 'Create, update and deactivate staff accounts', true),
    p('user.reset_password', 'core', 'Administration', 'Set a temporary password on a staff account', true),
    p('user.force_logout', 'core', 'Administration', 'Sign a staff account out of every device at once', true),
    p('user.manage_2fa', 'core', 'Administration', 'Enable or reset the authenticator of a staff account', true),
    p('role.read', 'core', 'Administration', 'View roles and permissions'),
    { ...p('role.manage', 'core', 'Administration', 'Create and customise roles', true), reserved: true },
    p('audit.read', 'core', 'Administration', 'Read the audit log'),
    p('support.ticket.read', 'core', 'Administration', 'View this facility\u2019s support tickets'),
    p('support.ticket.raise', 'core', 'Administration', 'Open a support ticket and reply on it'),
    p('numbering.manage', 'core', 'Administration', 'Configure MRN, invoice and token number patterns'),
    // Patients / MPI
    p('patient.read', 'core', 'Patients', 'Search and view patient demographics'),
    p('patient.register', 'core', 'Patients', 'Register new patients'),
    p('patient.update', 'core', 'Patients', 'Update patient demographics'),
    p('patient.merge', 'core', 'Patients', 'Merge duplicate patient records', true),
    p('patient.document.manage', 'core', 'Patients', 'Upload and remove patient documents'),
    p('patient.consent.manage', 'core', 'Patients', 'Capture and revoke patient consents'),
    p('patient.deceased.revert', 'core', 'Patients', 'Undo a patient wrongly recorded as deceased and reopen the record', true),
    // Appointments and queue
    p('appointment.read', 'opd', 'Appointments', 'View appointments and queues'),
    p('appointment.manage', 'opd', 'Appointments', 'Book, reschedule and cancel appointments'),
    p('appointment.read.own', 'opd', 'Appointments', 'View only own appointments (doctor scope)'),
    p('schedule.read', 'opd', 'Appointments', 'View doctor schedules'),
    p('schedule.manage', 'opd', 'Appointments', 'Create and update doctor schedules and blocks'),
    p('schedule.manage.own', 'opd', 'Appointments', 'Manage your own clinic schedule and leave'),
    p('token.issue', 'opd', 'Appointments', 'Issue queue tokens'),
    p('appointment.fee_alert.waive', 'opd', 'Appointments', 'Close a refund or fee alert without a refund or credit note, with a reason', true),
    // Clinical / EMR
    p('encounter.read', 'opd', 'Clinical', 'Read encounters for permitted patients'),
    p('encounter.read.own', 'opd', 'Clinical', 'Read only own or assigned patient encounters'),
    p('encounter.write', 'opd', 'Clinical', 'Create and edit encounters'),
    p('encounter.close', 'opd', 'Clinical', 'Close and finalise an encounter'),
    p('vitals.write', 'opd', 'Clinical', 'Record vitals'),
    p('diagnosis.write', 'opd', 'Clinical', 'Record ICD diagnoses'),
    p('rx.write', 'opd', 'Clinical', 'Create e-prescriptions'),
    p('rx.read', 'opd', 'Clinical', 'View prescriptions'),
    p('order.write', 'opd', 'Clinical', 'Place lab, radiology, procedure and referral orders'),
    p('order.read', 'opd', 'Clinical', 'View clinical orders'),
    p('order.late.post', 'ipd', 'Clinical', 'Place an order on a discharged stay or a closed ER case, with a reason', true),
    p('clinical.sensitive.read', 'core', 'Clinical', 'Read sensitivity-restricted notes', true),
    p('clinical.break_glass', 'core', 'Clinical', 'Emergency override to a restricted record', true),
    p('certificate.issue', 'opd', 'Clinical', 'Issue medical, fitness, sick-leave and death certificates'),
    p('encounter.cover', 'opd', 'Clinical', 'Write into or cancel another doctor’s encounter or prescription (locum and cover)', true),
    p('encounter.cosign', 'opd', 'Clinical', 'Co-sign consultations closed by a medical officer'),
    p('encounter.backdate', 'opd', 'Clinical', 'Open a consultation for an earlier clinic day, with a reason (downtime back-entry)', true),
    p('template.manage', 'opd', 'Clinical', 'Maintain note templates shared across the facility or a department'),
    p('emr.config.manage', 'opd', 'Clinical', 'Choose which EMR forms appear and which are required, hospital-wide and per department'),
    p('opd.fee.override', 'opd', 'Appointments', 'Start a consultation whose fee is not paid yet, with a reason', true),
    p('opd.fee.second_consultation', 'opd', 'Appointments', 'Bill a second consultation fee on one visit, with a reason', true),
    // IPD
    p('admission.read', 'ipd', 'IPD', 'View admissions'),
    p('admission.manage', 'ipd', 'IPD', 'Admit, transfer and discharge patients'),
    p('bed.read', 'ipd', 'IPD', 'View the bed board'),
    p('bed.manage', 'ipd', 'IPD', 'Maintain wards, rooms, beds and bed status'),
    p('cpoe.write', 'ipd', 'IPD', 'Enter physician orders (CPOE)'),
    p('emar.administer', 'ipd', 'IPD', 'Administer medication and sign the eMAR'),
    p('emar.read', 'ipd', 'IPD', 'View the medication administration record'),
    p('discharge.summary.write', 'ipd', 'IPD', 'Author and sign discharge summaries'),
    p('nursing.note.write', 'ipd', 'IPD', 'Write nursing notes and shift handovers'),
    p('ward.charge.manage', 'ipd', 'IPD', 'Maintain recurring ward charge rules and pause them for one admission'),
    p('dues.read', 'ipd', 'IPD', 'View the register of unpaid balances on closed admissions'),
    p('dues.manage', 'ipd', 'IPD', 'Record follow-up notes and promised dates against unpaid balances'),
    p('visitor.locate', 'ipd', 'IPD', 'Find which ward and bed an admitted patient is in, for visitors (name, ward and bed only)'),
    p('mortality.read', 'ipd', 'IPD', 'View the mortality report and the death certificates issued'),
    p('discharge.override', 'ipd', 'IPD', 'Discharge a patient whose bill is not cleared, with a reason'),
    // Emergency
    p('er.read', 'er', 'Emergency', 'View the ER board'),
    p('er.manage', 'er', 'Emergency', 'Register, triage and dispose ER cases'),
    // Operation theatre
    p('ot.read', 'ot', 'Operation Theatre', 'View OT schedule and cases'),
    p('ot.manage', 'ot', 'Operation Theatre', 'Schedule and document OT cases'),
    // Pharmacy
    p('pharmacy.pos', 'pharmacy', 'Pharmacy', 'Operate the pharmacy POS and dispense'),
    p('pharmacy.return.approve', 'pharmacy', 'Pharmacy', 'Approve a counter return above the counter limit, up to their own ceiling', true),
    p('rx.dispense', 'pharmacy', 'Pharmacy', 'Dispense against a prescription'),
    p('rx.substitute', 'pharmacy', 'Pharmacy', 'Dispense a different medicine than the prescription names, with a reason (never a controlled drug)'),
    p('pharmacy.return', 'pharmacy', 'Pharmacy', 'Process sales and ward returns'),
    p('pharmacy.read', 'pharmacy', 'Pharmacy', 'View pharmacy queues and sales'),
    p('indent.request', 'pharmacy', 'Pharmacy', 'Raise a ward medication indent'),
    p('indent.issue', 'pharmacy', 'Pharmacy', 'Approve and issue ward indents'),
    // Inventory and procurement
    p('product.read', 'inventory', 'Inventory', 'View the product and drug master'),
    p('product.manage', 'inventory', 'Inventory', 'Maintain the product master and formulary'),
    p('stock.read', 'inventory', 'Inventory', 'View stock levels and batches'),
    p('stock.adjust', 'inventory', 'Inventory', 'Adjust, write off and transfer stock', true),
    p('purchase.manage', 'inventory', 'Inventory', 'Purchase orders, GRN and supplier master'),
    p('store.manage', 'inventory', 'Inventory', 'Maintain stores and par levels'),
    p('purchase.grn', 'inventory', 'Inventory', 'Receive goods against a supplier (GRN) without managing purchasing'),
    p('purchase.return', 'inventory', 'Inventory', 'Return goods to a supplier and raise a debit note', true),
    p('stock.adjust.approve', 'inventory', 'Inventory', 'Approve stock write-offs and adjustments above the approval limit', true),
    p('stock.take.manage', 'inventory', 'Inventory', 'Run stock takes: count sheets, counting, variance and closing'),
    p('stock.batch.manage', 'inventory', 'Inventory', 'Quarantine, recall and release batches', true),
    p('stock.reprice', 'inventory', 'Inventory', 'Change the sale price of batches on hand', true),
    p('pharmacy.controlled', 'pharmacy', 'Pharmacy', 'Approve and issue indents that contain controlled drugs', true),
    p('supplier.payment.create', 'inventory', 'Inventory', 'Pay suppliers and allocate payments to goods received'),
    p('supplier.payment.approve', 'inventory', 'Inventory', 'Approve supplier payments above the approval limit, and reverse a supplier payment', true),
    p('pharmacy.report', 'pharmacy', 'Pharmacy', 'Pharmacy sales and margin, VAT return, wastage and stock movement reports without the facility financial reports'),
    // Laboratory
    p('lab.order.read', 'lab', 'Laboratory', 'View lab orders'),
    p('lab.order.create', 'lab', 'Laboratory', 'Place lab orders at the front desk'),
    p('lab.collect', 'lab', 'Laboratory', 'Record specimen collection and accession'),
    p('lab.result.enter', 'lab', 'Laboratory', 'Enter and technically validate results'),
    p('lab.result.verify', 'lab', 'Laboratory', 'Verify and release results', true),
    p('lab.master.manage', 'lab', 'Laboratory', 'Maintain test master, panels and ranges'),
    p('lab.result.amend', 'lab', 'Laboratory', 'Correct a verified or released result; the correction needs re-verification', true),
    p('lab.qc.override', 'lab', 'Laboratory', 'Report patient results while a QC run has failed, with a reason', true),
    p('lab.critical.acknowledge', 'lab', 'Laboratory', 'Record who was told a critical result and the read-back'),
    p('lab.release.override', 'lab', 'Laboratory', 'Release a report whose bill is unpaid, with a reason', true),
    p('lab.homecollection.assign', 'lab', 'Laboratory', 'Request and assign home sample collection'),
    p('lab.settings.manage', 'lab', 'Laboratory', 'Change the laboratory settings, including the home collection fee'),
    p('lab.order.unbilled', 'lab', 'Laboratory', 'Place a lab order without a bill, marked as credit, staff or free with a reason', true),
    p('lab.collect.identity.override', 'lab', 'Laboratory', 'Collect a specimen without the patient identity check, with a reason', true),
    p('lab.result.edit.approve', 'lab', 'Laboratory', 'Approve or reject a change a person made to an analyser value', true),
    p('lab.label.reprint.direct', 'lab', 'Laboratory', 'Reprint a specimen label without asking for approval'),
    p('lab.label.reprint.approve', 'lab', 'Laboratory', 'Approve a request to reprint a specimen label', true),
    p('lab.instrument.view', 'lab', 'Laboratory', 'See analysers, their message logs and parked results'),
    p('lab.instrument.manage', 'lab', 'Laboratory', 'Register analysers, issue their keys, map their codes and replay their messages'),
    // Radiology
    p('radiology.worklist', 'radiology', 'Radiology', 'View and update the modality worklist'),
    p('radiology.order.create', 'radiology', 'Radiology', 'Place imaging orders at the front desk'),
    p('radiology.report.write', 'radiology', 'Radiology', 'Author radiology reports'),
    p('radiology.report.verify', 'radiology', 'Radiology', 'Verify and release radiology reports', true),
    p('radiology.master.manage', 'radiology', 'Radiology', 'Maintain modalities and study master'),
    p('radiology.critical.acknowledge', 'radiology', 'Radiology', 'Record who was told a critical imaging finding'),
    p('radiology.order.unbilled', 'radiology', 'Radiology', 'Place an imaging order without a bill, marked as credit, staff or free with a reason', true),
    p('radiology.release.override', 'radiology', 'Radiology', 'Release an imaging report whose bill is unpaid or missing, with a reason', true),
    p('radiology.import.manage', 'radiology', 'Radiology', 'Register image import agents and issue their keys'),
    p('radiology.second_opinion', 'radiology', 'Radiology', 'Ask for, or give, a second opinion on a study'),
    // Billing
    p('charge.read', 'billing', 'Billing', 'View the charge master and tariffs'),
    p('charge.manage', 'billing', 'Billing', 'Maintain charge items, tariffs and packages', true),
    p('invoice.read', 'billing', 'Billing', 'View invoices'),
    p('invoice.create', 'billing', 'Billing', 'Create and issue invoices'),
    p('invoice.cancel', 'billing', 'Billing', 'Cancel an issued invoice', true),
    p('invoice.credit_note', 'billing', 'Billing', 'Raise a credit note against an issued or paid invoice', true),
    p('invoice.line.remove', 'billing', 'Billing', 'Remove a wrongly charged line from an unpaid invoice', true),
    p('invoice.price.override', 'billing', 'Billing', 'Bill a service that is not on the charge master, or at more than its tariff price', true),
    p('invoice.tax_override', 'billing', 'Billing', 'Change how VAT is applied to a bill line, with a reason', true),
    p('payment.collect', 'billing', 'Billing', 'Collect payments'),
    p('payment.refund', 'billing', 'Billing', 'Request a refund', true),
    p('payment.refund.approve', 'billing', 'Billing', 'Approve a refund', true),
    p('discount.apply', 'billing', 'Billing', 'Give a discount, up to the limit set for them in Accounts'),
    p('discount.approve', 'billing', 'Billing', 'Approve a colleague’s discount, up to their own limit', true),
    p('shift.manage', 'billing', 'Billing', 'Open and close cashier shifts'),
    p('shift.supervise', 'billing', 'Billing', 'Close another cashier’s shift and read any cashier’s shift report', true),
    p('deposit.manage', 'billing', 'Billing', 'Collect and refund IPD advances'),
    // Insurance
    p('payer.manage', 'insurance', 'Insurance', 'Maintain payers, plans and coverage rules'),
    p('coverage.verify', 'insurance', 'Insurance', 'Verify patient coverage'),
    p('claim.read', 'insurance', 'Insurance', 'View claims'),
    p('claim.manage', 'insurance', 'Insurance', 'Create, submit and track claims'),
    p('claim.settle', 'insurance', 'Accounts', 'Decide who pays what an insurer refused or short-paid, and close claims', true),
    p('remittance.post', 'insurance', 'Accounts', 'Record a payer remittance and post it to claims, or take a posting back', true),
    p('payer_invoice.issue', 'insurance', 'Accounts', 'Issue the monthly tax invoice to an insurer for its share of the bills', true),
    p('preauth.manage', 'insurance', 'Insurance', 'Raise and record pre-authorisations'),
    p('preauth.resend', 'insurance', 'Insurance', 'Send a pre-authorisation line the insurer has already answered again, with a reason', true),
    p('integration.read', 'core', 'Integrations', 'View external service connections and the message log'),
    p('integration.manage', 'core', 'Integrations', 'Configure external services, credentials and code mapping', true),
    p('nphies.use', 'nphies', 'Integrations', 'Check eligibility and send pre-authorisations and claims through NPHIES'),
    p('nphies.eligibility', 'nphies', 'NPHIES', 'Ask the insurer through NPHIES whether a policy is in force'),
    p('nphies.preauth.send', 'nphies', 'NPHIES', 'Send pre-authorisation requests to the insurer through NPHIES'),
    p('nphies.preauth.resend', 'nphies', 'NPHIES', 'Send a pre-authorisation line the insurer has already answered again, with a reason', true),
    p('nphies.claim.send', 'nphies', 'NPHIES', 'Send claims to the insurer through NPHIES'),
    p('nphies.status', 'nphies', 'NPHIES', 'Ask the insurer for the current answer on a claim or pre-authorisation'),
    p('nphies.communication', 'nphies', 'NPHIES', 'Answer or close an insurer request for more information'),
    p('nphies.payment.notice', 'nphies', 'NPHIES', 'Tell the insurer a reported payment arrived'),
    p('nphies.poll.now', 'nphies', 'NPHIES', 'Check the insurer for answers now'),
    p('nphies.deadletter.apply', 'nphies', 'NPHIES', 'Apply an insurer message that could not be applied when it arrived'),
    p('nphies.messages.read', 'nphies', 'NPHIES', 'Open the record of an NPHIES exchange and see what is waiting'),
    p('nphies.cancel', 'nphies', 'NPHIES', 'Ask the insurer to cancel a claim or pre-authorisation, or stop a queued NPHIES message', true),
    p('nphies.settings', 'nphies', 'NPHIES', 'Set the NPHIES address, credentials and insurer code mapping', true),
    p('nphies.archive.read', 'nphies', 'NPHIES', 'Open the local archive of NPHIES messages exactly as they were sent and received', true),
    p('nphies.inbound.link', 'nphies', 'NPHIES', 'Link an approval an insurer sent unasked to the patient and policy it names'),
    p('nphies.inbound.override', 'nphies', 'NPHIES', 'Link an insurer approval whose patient or policy does not match what is on file, with a reason', true),
    p('wasfaty.use', 'wasfaty', 'Integrations', 'Download, authorise, dispense and claim Wasfaty e-prescriptions'),
    // Accounting
    p('gl.read', 'accounting', 'Accounting', 'View ledgers and financial statements'),
    p('gl.manage', 'accounting', 'Accounting', 'Maintain the chart of accounts and post journals', true),
    p('payout.manage', 'accounting', 'Accounting', 'Compute and approve doctor payouts', true),
    p('authority.read', 'core', 'Accounts', 'View money authority: discount and approval limits, thresholds and their history'),
    p('authority.manage', 'core', 'Accounts', 'Change money authority: discount and approval limits, thresholds and two-person rules', true),
    p('doctor_share.read', 'billing', 'Accounts', 'View doctor revenue shares and their history', true),
    p('doctor_share.manage', 'billing', 'Accounts', 'Change doctor revenue shares', true),
    // HR
    p('hr.employee.read', 'hr', 'HR', 'View employee records'),
    p('hr.employee.manage', 'hr', 'HR', 'Maintain employee records and contracts', true),
    p('hr.roster.manage', 'hr', 'HR', 'Maintain duty rosters and shifts'),
    p('hr.attendance.manage', 'hr', 'HR', 'Record and correct attendance'),
    p('hr.leave.approve', 'hr', 'HR', 'Approve leave requests'),
    p('hr.payroll.run', 'hr', 'HR', 'Run payroll and generate payslips', true),
    p('hr.self.read', 'hr', 'HR', 'View own HR record, attendance and payslips'),
    // MRD
    p('mrd.read', 'mrd', 'Medical Records', 'View record completion worklists'),
    p('mrd.code', 'mrd', 'Medical Records', 'Perform ICD coding'),
    p('mrd.release', 'mrd', 'Medical Records', 'Release records against a consented request', true),
    // Reports
    p('report.read', 'reports', 'Reports', 'Run reports within own module scope'),
    p('report.read.all', 'reports', 'Reports', 'Run every report'),
    p('report.financial', 'reports', 'Reports', 'Run financial reports'),
    p('dashboard.read', 'reports', 'Reports', 'View role dashboards'),
    // Notifications
    p('notification.template.manage', 'notifications', 'Notifications', 'Maintain message templates'),
    p('notification.send', 'notifications', 'Notifications', 'Send ad-hoc notifications'),
    p('notification.read', 'notifications', 'Notifications', 'View the notification log'),
    // Support modules
    p('task.read', 'tasks', 'Tasks', 'View assigned tasks'),
    p('task.manage', 'tasks', 'Tasks', 'Assign and close tasks'),
    p('ambulance.manage', 'ambulance', 'Ambulance', 'Fleet, dispatch and trip logs'),
    p('dietary.manage', 'dietary', 'Dietary', 'Diet orders and meal planning'),
    p('housekeeping.manage', 'housekeeping', 'Housekeeping', 'Linen and cleaning tasks'),
    p('feedback.read', 'feedback', 'Feedback', 'View feedback and complaints'),
    p('feedback.manage', 'feedback', 'Feedback', 'Route and resolve complaints'),
    p('blood_bank.manage', 'blood_bank', 'Blood Bank', 'Donors, stock, cross-match and issue'),
    p('maternity.manage', 'maternity', 'Labour & Delivery', 'Partograph, delivery and newborn records'),
    p('therapy.manage', 'dialysis', 'Therapy Units', 'Schedule and chart dialysis, chemotherapy and physiotherapy sessions'),
    p('dental.manage', 'dental', 'Dental', 'Tooth charting and treatment plans'),
];
export const PERMISSION_CODES = PERMISSIONS.map((x) => x.code);
export const NPHIES_OPERATION_PERMISSIONS = {
    eligibility: 'nphies.eligibility',
    priorauth: 'nphies.preauth.send',
    claim: 'nphies.claim.send',
    cancel: 'nphies.cancel',
    'status-check': 'nphies.status',
    communication: 'nphies.communication',
    'payment-notice': 'nphies.payment.notice',
    poll: 'nphies.poll.now',
};
export function nphiesOperationPermission(operation) {
    return NPHIES_OPERATION_PERMISSIONS[operation] ?? 'nphies.settings';
}
export function providerSettingsPermission(provider) {
    return provider === 'nphies' ? 'nphies.settings' : 'integration.manage';
}
const PERMISSION_INDEX = new Map(PERMISSIONS.map((x) => [x.code, x]));
export function getPermission(code) {
    return PERMISSION_INDEX.get(code);
}
export const SUPER_ADMIN_ROLE_CODE = 'super_admin';
export const ADMINISTRATIVE_PERMISSIONS = [
    'user.manage',
    'role.manage',
    'tenant.settings.manage',
    'integration.manage',
];
export const SENSITIVE_PERMISSION_CODES = new Set(PERMISSIONS.filter((x) => x.sensitive).map((x) => x.code));
export function isReservedPermission(code) {
    return PERMISSION_INDEX.get(code)?.reserved === true;
}
export function isSensitivePermission(code) {
    return code === '*' || SENSITIVE_PERMISSION_CODES.has(code);
}
export function permissionsForModules(modules) {
    const set = new Set(modules);
    return PERMISSIONS.filter((x) => x.modules.some((module) => set.has(module))).map((x) => x.code);
}
export function permissionState(code, enabled, retired = new Set()) {
    const descriptor = PERMISSION_INDEX.get(code);
    if (!descriptor || descriptor.reserved)
        return 'off';
    if (descriptor.modules.some((module) => module === 'core' || enabled.has(module)))
        return 'live';
    if (descriptor.access === 'read' && descriptor.modules.some((module) => retired.has(module)))
        return 'read_only';
    return 'off';
}
export function isPermissionUsable(code, enabled, retired = new Set()) {
    const descriptor = PERMISSION_INDEX.get(code);
    if (!descriptor)
        return false;
    if (descriptor.reserved)
        return true;
    return permissionState(code, enabled, retired) !== 'off';
}
export function isClinicalPermission(code) {
    return PERMISSION_INDEX.get(code)?.clinical === true;
}
//# sourceMappingURL=permissions.js.map